import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { inventoryActions } from "../../sagas/inventorySlice";
import { useFormik } from "formik";
import {
    Container, Card, CardBody, Row, Col, Button, Table, Input
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import stockService from "../../services/stockService";
import ProductSelectModal from "./ProductSelectModal";
import { productActions } from "../../sagas/productSlice";
import { FaArrowLeft, FaClipboardCheck } from "react-icons/fa";
import { entrepotActions } from "../../sagas/entrepotSlice";
import Select from "react-select";
import { preventScroll } from "../../helpers/utility";

const InventoryFormPage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentInventory } = useSelector(state => state.inventory);
    const { entrepots } = useSelector(state => state.entrepots);

    const products = useSelector(state => state.products.products);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalProducts, setModalProducts] = useState([]);
    const [searchText, setSearchText] = useState("");

    useEffect(() => {
        if (id) {
            dispatch(inventoryActions.getInventory(id));
        } else {
            dispatch(inventoryActions.clearCurrentInventory()); // 🔥 important
        }
    }, [id]);

    useEffect(() => {
        dispatch(entrepotActions.list());
    }, []);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: currentInventory || {
            dateInventaire: new Date().toISOString().split("T")[0], // 📅 today
            entrepotId: "",
            lignes: []
        },
        onSubmit: (values) => {
            if (id) {
                dispatch(inventoryActions.updateInventory({
                    id,
                    data: values,
                    onSuccess: () => navigate("/inventories")
                }));
            } else {
                dispatch(inventoryActions.createInventory({
                    data: values,
                    onSuccess: () => navigate("/inventories")
                }));
            }
        }
    });

    const isLocked = currentInventory?.status === "VALIDE";


    const entrepotOptions = entrepots?.map(entrepot => ({
        value: entrepot.id,
        label: entrepot.code + ' - ' + entrepot.libelle,
    })) ?? [];

    const isEntrepotSelected = !!formik.values.entrepotId;

    const handleProductSelected = async (product) => {

        // Get theoretical stock for selected warehouse
        const { data } = await stockService.getStock(product.id, formik.values.entrepotId);

        const existingIndex = formik.values.lignes.findIndex(
            l => l.articleId === product.id
        );

        if (existingIndex >= 0) {
            // Already exists → increase physical qty
            const updated = [...formik.values.lignes];
            updated[existingIndex].quantitePhysique =
                Number(updated[existingIndex].quantitePhysique) + 1;
            formik.setFieldValue("lignes", updated);
        } else {
            // New line
            formik.setFieldValue("lignes", [
                ...formik.values.lignes,
                {
                    articleId: product.id,
                    articleCode: product.code,
                    articleLibelle: product.libelle,
                    quantiteTheorique: data.quantiteDisponible || 0,
                    quantitePhysique: 1
                }
            ]);
        }

        setSearchText("");
    };

    return (
        <Container>
            <Row className="mb-4">
                <Col>
                    <Card className="shadow-sm">
                        <CardBody className="d-flex justify-content-between align-items-center">
                            <h4 className="mb-0 d-flex align-items-center">
                                <FaClipboardCheck className="me-2 text-primary" />
                                {id ? "Modifier inventaire" : "Nouvel inventaire"}
                            </h4>
                            <div className="d-flex align-items-center gap-2">
                                <Button
                                    color="secondary"
                                    outline
                                    onClick={() => navigate("/inventories")}
                                >
                                    <FaArrowLeft className="me-1" />
                                    Retour
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            {isLocked && (
                <div className="alert alert-info mt-2">
                    Cet inventaire est validé et ne peut plus être modifié.
                </div>
            )}
            <Card>
                <CardBody>
                    <form onSubmit={formik.handleSubmit}>
                        <Row>
                            <Col md={4}>
                                <label>Date</label>
                                <Input
                                    type="date"
                                    name="dateInventaire"
                                    value={formik.values.dateInventaire}
                                    onChange={formik.handleChange}
                                    disabled={isLocked}
                                />
                            </Col>
                            <Col md={4}>
                                <label>Entrepôt</label>

                                <Select
                                    id="entrepotId"
                                    options={entrepotOptions}
                                    value={entrepotOptions.find(option => option.value === formik.values.entrepotId)}
                                    onChange={(selectedOption) => formik.setFieldValue('entrepotId', selectedOption.value)}
                                    placeholder="Sélectionner..."
                                    isSearchable
                                    isDisabled={isLocked}
                                />
                            </Col>
                        </Row>

                        <hr />
                        {!isEntrepotSelected && (
                            <div className="text-warning mb-2">
                                Sélectionnez d'abord un entrepôt pour commencer le scan.
                            </div>
                        )}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Input
                                    placeholder="Scanner code barre ou rechercher article..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();   // 🛑 STOP FORM SUBMIT

                                            if (!searchText.trim()) return;

                                            dispatch(productActions.findAll({
                                                search: searchText,
                                                page: 0,
                                                size: 5,
                                                onSuccess: (results) => {
                                                    if (results.length === 1) {
                                                        handleProductSelected(results[0]);
                                                    } else if (results.length > 1) {
                                                        setModalProducts(results);
                                                        setModalOpen(true);
                                                    } else {
                                                        Swal.fire("Aucun article trouvé");
                                                    }
                                                }
                                            }));
                                        }
                                    }}
                                    disabled={!isEntrepotSelected}
                                />
                            </Col>
                        </Row>

                        <Table bordered>
                            <thead>
                                <tr>
                                    <th>Article</th>
                                    <th>Théorique</th>
                                    <th>Physique</th>
                                    <th>Écart</th>
                                </tr>
                            </thead>
                            <tbody>
                                {formik.values.lignes.map((l, i) => (
                                    <tr key={i}>
                                        <td>{l.articleCode} - {l.articleLibelle}</td>
                                        <td>{l.quantiteTheorique}</td>
                                        <td>
                                            <Input
                                                type="number"
                                                name={`lignes.${i}.quantitePhysique`}
                                                value={l.quantitePhysique}
                                                onChange={formik.handleChange}
                                                onFocus={(e) => {
                                                    e.target.select();
                                                    e.target.addEventListener('wheel', preventScroll, { passive: false });
                                                }}
                                                disabled={isLocked}
                                            />
                                        </td>
                                        <td
                                            className={
                                                (l.quantiteTheorique < 0
                                                    ? l.quantitePhysique
                                                    : l.quantitePhysique - l.quantiteTheorique) !== 0
                                                    ? "text-danger fw-bold"
                                                    : ""
                                            }
                                        >
                                            {l.quantiteTheorique < 0
                                                ? l.quantitePhysique
                                                : l.quantitePhysique - l.quantiteTheorique}
                                        </td>

                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        {!isLocked && (
                            <Button type="submit" color="success">Enregistrer</Button>
                        )}
                    </form>
                </CardBody>
            </Card>

            <ProductSelectModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                products={modalProducts}
                onSelect={handleProductSelected}
            />
        </Container>
    );
};

export default InventoryFormPage;
